import React, { useState, useEffect, useRef } from 'react';
import { Sun, Moon, MapPin, X, ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Sparkles, Calendar } from 'lucide-react';

import { Language } from '../types/kundali';
import { usePanchang } from '../hooks/usePanchang';
import { getUserLocationPref, VerifiedLocation } from '../services/geoService';
import { formatDateTimeWithTz } from '../utils/dateUtils';

interface TodaysPanchangWidgetProps {
  initialCity?: string;
  lang?: Language | string;
  onCityChange?: (city: string) => void;
  className?: string;
}

export const TodaysPanchangWidget: React.FC<TodaysPanchangWidgetProps> = ({
  initialCity,
  lang = 'en',
  onCityChange,
  className = ''
}) => {
  const isHi = lang === 'hi';
  const [city, setCity] = useState(() => initialCity || getUserLocationPref().placeName);
  const [inputCity, setInputCity] = useState(() => initialCity || getUserLocationPref().placeName);
  const [isPanchangOpen, setIsPanchangOpen] = useState(false);
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [showAllTithis, setShowAllTithis] = useState(false);
  const [showAllNakshatras, setShowAllNakshatras] = useState(false);
  const [showAllYogas, setShowAllYogas] = useState(false);
  const [showAllKaranas, setShowAllKaranas] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Bind calculation directly to live system clock
  const panchang = usePanchang(city);

  // Sync when prop changes
  useEffect(() => {
    if (initialCity && initialCity !== city) {
      setCity(initialCity);
      setInputCity(initialCity);
    }
  }, [initialCity]);

  // Sync when global user location preference changes
  useEffect(() => {
    const handleLocationChange = (e: Event) => {
      const detail = (e as CustomEvent<VerifiedLocation>).detail;
      if (detail && !initialCity) {
        setCity(detail.placeName);
        setInputCity(detail.placeName);
      }
    };
    window.addEventListener('astro_location_pref_changed', handleLocationChange);
    return () => window.removeEventListener('astro_location_pref_changed', handleLocationChange);
  }, [initialCity]);

  // Outside click, Escape to dismiss, and WCAG 2.1 AA Focus Trapping
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPanchangOpen) {
        setIsPanchangOpen(false);
        setIsEditingLocation(false);
      }
      if (e.key === 'Tab' && isPanchangOpen && drawerRef.current) {
        const focusableElements = drawerRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
          if (e.shiftKey && document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        isPanchangOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(e.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsPanchangOpen(false);
        setIsEditingLocation(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPanchangOpen]);

  const handleSaveLocation = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = inputCity.trim();
    if (clean) {
      setCity(clean);
      setIsEditingLocation(false);
      if (onCityChange) onCityChange(clean);
    }
  };

  const selectCityPreset = (preset: string) => {
    setCity(preset);
    setInputCity(preset);
    setIsEditingLocation(false);
    if (onCityChange) onCityChange(preset);
  };

  return (
    <div ref={containerRef} className={`relative shrink-0 ${className}`}>
      {/* Minimal Panchang Icon Trigger */}
      <div className="relative group shrink-0">
        <button
          type="button"
          onClick={() => {
            setIsPanchangOpen((prev) => !prev);
            if (isPanchangOpen) setIsEditingLocation(false);
          }}
          aria-label="Today's Panchang"
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border transition-all text-xs cursor-pointer ${
            isPanchangOpen
              ? 'bg-astro-primary text-astro-bg border-astro-primary'
              : 'bg-astro-card text-astro-primary border-astro-border hover:bg-astro-secondary-surface'
          }`}
        >
          <span className="text-sm leading-none" role="img" aria-label="Panchang Calendar">📅</span>
          <span className="text-[9px] text-astro-accent transition-transform duration-200">
            {isPanchangOpen ? '▴' : '▾'}
          </span>
        </button>

        {/* Micro-Tooltip on Hover */}
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 hidden group-hover:block z-50 pointer-events-none">
          <div className="bg-astro-primary text-astro-bg text-[10px] font-medium py-0.5 px-2 rounded shadow-md whitespace-nowrap">
            Today's Panchang
          </div>
        </div>
      </div>

      {/* 2. EXPANDED UTILITY DRAWER DIRECTLY BENEATH THE HEADER */}
      {isPanchangOpen && (
        <div
          id="todays-panchang-drawer"
          ref={drawerRef}
          role="region"
          aria-label="Today's Panchang & Fasting Timings Drawer"
          className="fixed left-0 right-0 top-[56px] sm:top-[62px] md:top-[66px] z-50 bg-astro-card border-b border-astro-border shadow-xl animate-fade-in backdrop-blur-md bg-astro-card/98 transition-all"
        >
          <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-8 py-3.5 sm:py-4">
            {/* Drawer Top Strip: Title & Quick Dismiss */}
            <div className="flex items-center justify-between border-b border-astro-border pb-2.5 mb-3">
              <div className="flex items-center gap-2">
                <span className="text-base sm:text-lg">🪔</span>
                <div>
                  <h3 className="font-serif font-bold text-sm sm:text-base text-astro-primary flex items-center gap-2">
                    {isHi ? 'दैनिक पञ्चाङ्ग एवं व्रत-पारणा' : "Today's Panchang & Fasting Governance"}
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-astro-bg text-astro-accent border border-astro-border">
                      {panchang.dateString}
                    </span>
                  </h3>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setIsPanchangOpen(false);
                  setIsEditingLocation(false);
                }}
                className="inline-flex items-center gap-1 text-xs font-semibold text-astro-secondary-text hover:text-astro-primary bg-astro-bg px-2.5 py-1 rounded-md border border-astro-border cursor-pointer hover:bg-[#FAF6ED] transition"
                aria-label="Close drawer"
              >
                <span>{isHi ? 'बंद करें' : 'Close'}</span>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Two Balanced Columns Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
              {/* LEFT COLUMN: Location & Solar Times (5 cols) */}
              <div className="lg:col-span-5 flex flex-col gap-2.5 sm:gap-3 bg-astro-bg p-3 sm:p-3.5 rounded-xl border border-astro-border shadow-2xs">
                {/* Location Selector & Confirm Bar */}
                <div className="border-b border-astro-border pb-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-astro-muted flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-astro-accent" />
                      {isHi ? 'गणना संदर्भ स्थान' : 'Calculation Reference Location'}
                    </span>
                    {!isEditingLocation ? (
                      <button
                        type="button"
                        onClick={() => setIsEditingLocation(true)}
                        className="text-[11px] font-bold text-astro-accent hover:underline cursor-pointer"
                      >
                        {isHi ? 'बदलें' : 'Change City'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsEditingLocation(false)}
                        className="text-[11px] text-astro-muted hover:underline cursor-pointer"
                      >
                        {isHi ? 'रद्द करें' : 'Cancel'}
                      </button>
                    )}
                  </div>

                  {isEditingLocation ? (
                    <div className="space-y-2">
                      <form onSubmit={handleSaveLocation} className="flex gap-1.5">
                        <input
                          type="text"
                          value={inputCity}
                          onChange={(e) => setInputCity(e.target.value)}
                          placeholder="e.g. Indore, Delhi, Mumbai, Varanasi..."
                          className="flex-1 px-2.5 py-1 text-xs rounded-lg border border-astro-border bg-[#FAF6ED] text-astro-text focus:outline-none focus:ring-1 focus:ring-astro-primary"
                          autoFocus
                        />
                        <button
                          type="submit"
                          className="px-3 py-1 bg-astro-primary hover:bg-[#0E3433] text-white text-xs font-bold rounded-lg transition cursor-pointer"
                        >
                          {isHi ? 'सहेजें' : 'Confirm'}
                        </button>
                      </form>

                      <div className="flex flex-wrap gap-1 items-center pt-1">
                        <span className="text-[10px] text-astro-muted font-semibold">{isHi ? 'त्वरित:' : 'Quick:'}</span>
                        {['Indore', 'Delhi', 'Mumbai', 'Varanasi', 'Bengaluru', 'Jaipur'].map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => selectCityPreset(p)}
                            className={`text-[10px] px-1.5 py-0.5 rounded border transition cursor-pointer ${
                              city.toLowerCase().includes(p.toLowerCase())
                                ? 'bg-astro-primary text-white border-astro-primary'
                                : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border hover:bg-astro-border'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-astro-primary">{city}</span>
                      <span className="text-[10px] font-mono text-astro-muted">
                        {panchang.coordinates.lat.toFixed(2)}° N, {panchang.coordinates.lng.toFixed(2)}° E
                      </span>
                    </div>
                  )}
                </div>

                {/* Pitru Paksha Contextual Notice (When Active) */}
                {panchang.pitruPaksha?.isActive && (
                  <div className="p-2.5 rounded-xl bg-amber-100/70 border border-amber-300 text-xs flex items-start gap-2 animate-fade-in">
                    <Sparkles className="w-4 h-4 text-amber-800 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <strong className="text-amber-900 font-serif block text-xs">
                          {isHi ? (panchang.pitruPaksha.observanceNameHi || 'पितृ पक्ष (श्राद्ध पक्ष) सक्रिय') : 'Pitru Paksha (Shraddha Paksha) Active'}
                        </strong>
                        <span className="text-[10px] font-bold px-2 py-0.2 rounded bg-amber-200/80 text-amber-900 border border-amber-300 font-mono">
                          {isHi ? (panchang.pitruPaksha.shraddhaTithiNameHi || panchang.pitruPaksha.shraddhaTithiNameEn) : panchang.pitruPaksha.shraddhaTithiNameEn}
                        </span>
                      </div>
                      {panchang.pitruPaksha.kutupaMuhurta && (
                        <div className="mt-1.5 text-[10px] sm:text-[11px] font-mono text-amber-900 bg-amber-50/90 p-1.5 rounded-lg border border-amber-200 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span>{isHi ? 'कुतुप काल: ' : 'Kutupa Muhurta: '}<strong>{panchang.pitruPaksha.kutupaMuhurta.start} – {panchang.pitruPaksha.kutupaMuhurta.end}</strong></span>
                          <span>{isHi ? 'रोहिण: ' : 'Rohina: '}<strong>{panchang.pitruPaksha.rohinaMuhurta?.start} – {panchang.pitruPaksha.rohinaMuhurta?.end}</strong></span>
                          <span>{isHi ? 'अपराह्न: ' : 'Aparahna: '}<strong>{panchang.pitruPaksha.aparahnaKala?.start} – {panchang.pitruPaksha.aparahnaKala?.end}</strong></span>
                        </div>
                      )}
                      <span className="text-[11px] text-amber-800 block mt-1 leading-snug">
                        {isHi ? (panchang.pitruPaksha.tarpanGuidanceHi || panchang.pitruPaksha.descriptionHi) : (panchang.pitruPaksha.tarpanGuidanceEn || panchang.pitruPaksha.description)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Surya Sankranti & Punya Kaal Context (When Active) */}
                {panchang.sankranti?.isSankrantiDay && (
                  <div className="p-2.5 rounded-xl bg-orange-50/80 border border-orange-300 text-xs flex items-start gap-2 animate-fade-in">
                    <Sun className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <strong className="text-orange-950 font-serif block text-xs">
                          {isHi ? (panchang.sankranti.sankrantiNameHi || 'सूर्य संक्रांति') : (panchang.sankranti.sankrantiNameEn || 'Surya Sankranti')}
                        </strong>
                        <span className="text-[10px] font-bold px-2 py-0.2 rounded bg-orange-200 text-orange-900 border border-orange-300 font-mono">
                          {isHi ? `संक्रमण: ${panchang.sankranti.formattedIngressTime}` : `Ingress: ${panchang.sankranti.formattedIngressTime}`}
                        </span>
                      </div>
                      {panchang.sankranti.formattedPunyaKaal && (
                        <div className="mt-1.5 text-[10px] sm:text-[11px] font-mono text-orange-900 bg-orange-100/60 p-1.5 rounded-lg border border-orange-200 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span>{isHi ? 'पुण्य काल: ' : 'Punya Kaal: '}<strong>{panchang.sankranti.formattedPunyaKaal}</strong></span>
                          {panchang.sankranti.formattedMahaPunyaKaal && (
                            <span>{isHi ? 'महापुण्य काल: ' : 'Maha Punya Kaal: '}<strong>{panchang.sankranti.formattedMahaPunyaKaal}</strong></span>
                          )}
                        </div>
                      )}
                      <span className="text-[11px] text-orange-800 block mt-1 leading-snug">
                        {isHi ? panchang.sankranti.significanceHi : panchang.sankranti.significanceEn}
                      </span>
                    </div>
                  </div>
                )}

                {/* Solar Day Markers: Sunrise & Sunset */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 sm:p-2.5 rounded-lg bg-[#FAF6ED] border border-astro-border flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-astro-accent/15 text-astro-accent flex items-center justify-center shrink-0">
                      <Sun className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-bold">
                        {isHi ? 'सूर्योदय' : 'Sunrise'}
                      </span>
                      <span className="text-xs sm:text-sm font-bold text-astro-text font-mono">
                        {panchang.sunrise}
                      </span>
                    </div>
                  </div>

                  <div className="p-2 sm:p-2.5 rounded-lg bg-[#FAF6ED] border border-astro-border flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-astro-primary/15 text-astro-primary flex items-center justify-center shrink-0">
                      <Moon className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-bold">
                        {isHi ? 'सूर्यास्त' : 'Sunset'}
                      </span>
                      <span className="text-xs sm:text-sm font-bold text-astro-text font-mono">
                        {panchang.sunset}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Key Time Windows: Abhijit Muhurat & Rahu Kaal */}
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-50/70 border border-emerald-200 text-xs">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                      <div>
                        <span className="font-bold text-emerald-900 block text-[11px]">
                          {isHi ? 'अभिजित मुहूर्त (अति शुभ)' : 'Abhijit Muhurat (Auspicious)'}
                        </span>
                        <span className="text-[10px] text-emerald-700">
                          {isHi ? 'विजय एवं शुभ आरंभ हेतु' : 'Best for new initiatives & deeds'}
                        </span>
                      </div>
                    </div>
                    <span className="font-mono font-bold text-emerald-900 text-xs whitespace-nowrap">
                      {panchang.abhijitMuhurat}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-2 rounded-lg bg-amber-50/70 border border-amber-200 text-xs">
                    <div className="flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                      <div>
                        <span className="font-bold text-amber-900 block text-[11px]">
                          {isHi ? 'राहु काल (वर्ज्य / त्यागें)' : 'Rahu Kaal (Avoid)'}
                        </span>
                        <span className="text-[10px] text-amber-700">
                          {isHi ? 'शुभ कार्य आरंभ न करें' : 'Avoid major auspicious ventures'}
                        </span>
                      </div>
                    </div>
                    <span className="font-mono font-bold text-amber-900 text-xs whitespace-nowrap">
                      {panchang.rahuKaal}
                    </span>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN: Panchang Essentials (Five Limbs) & Fasting/Parana (7 cols) */}
              <div className="lg:col-span-7 flex flex-col gap-2.5 sm:gap-3">
                {/* 5 Panchang Limbs Grid */}
                <div className="bg-astro-bg p-3 sm:p-3.5 rounded-xl border border-astro-border shadow-2xs">
                  <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-astro-muted mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                    {isHi ? 'पञ्चाङ्ग पञ्च-अंग (दैनिक ग्रह स्थिति)' : "Panchang Essentials (Five Limbs of Time)"}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border relative">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-semibold">
                          {isHi ? 'तिथि' : 'Tithi'}
                        </span>
                        {panchang.tithiTransitions && panchang.tithiTransitions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setShowAllTithis(prev => !prev)}
                            className="text-[9px] text-astro-accent hover:underline font-medium cursor-pointer"
                          >
                            {showAllTithis ? 'Show Active' : `${panchang.tithiTransitions.length} Tithis ▾`}
                          </button>
                        )}
                      </div>

                      {showAllTithis && panchang.tithiTransitions ? (
                        <div className="space-y-1.5 mt-1 pt-1 border-t border-astro-border/60 animate-fade-in max-h-40 overflow-y-auto">
                          {panchang.tithiTransitions.map((tr, tIdx) => (
                            <div key={tIdx} className="text-[10px] bg-white p-1.5 rounded border border-astro-border/50">
                              <strong className="text-astro-primary font-serif block truncate">{tr.name}</strong>
                              <span className="text-[9px] text-astro-text block font-mono">From: {formatDateTimeWithTz(tr.startDateTime)}</span>
                              <span className="text-[9px] text-astro-text block font-mono">To:   {formatDateTimeWithTz(tr.endDateTime)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <strong className="text-astro-primary font-serif block text-xs truncate mt-0.5">
                            {panchang.tithi}
                          </strong>
                          <span className="text-[10px] text-astro-secondary-text block truncate">
                            {panchang.tithiEnd}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border relative">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-semibold">
                          {isHi ? 'नक्षत्र' : 'Nakshatra'}
                        </span>
                        {panchang.nakshatraTransitions && panchang.nakshatraTransitions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setShowAllNakshatras(prev => !prev)}
                            className="text-[9px] text-astro-accent hover:underline font-medium cursor-pointer"
                          >
                            {showAllNakshatras ? 'Show Active' : `${panchang.nakshatraTransitions.length} Nakshatras ▾`}
                          </button>
                        )}
                      </div>

                      {showAllNakshatras && panchang.nakshatraTransitions ? (
                        <div className="space-y-1.5 mt-1 pt-1 border-t border-astro-border/60 animate-fade-in max-h-40 overflow-y-auto">
                          {panchang.nakshatraTransitions.map((tr, nIdx) => (
                            <div key={nIdx} className="text-[10px] bg-white p-1.5 rounded border border-astro-border/50">
                              <strong className="text-astro-primary font-serif block truncate">{tr.name} (Pada {tr.pada})</strong>
                              <span className="text-[9px] text-astro-text block font-mono">From: {formatDateTimeWithTz(tr.startDateTime)}</span>
                              <span className="text-[9px] text-astro-text block font-mono">To:   {formatDateTimeWithTz(tr.endDateTime)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <strong className="text-astro-text block text-xs truncate mt-0.5">
                            {panchang.nakshatra}
                          </strong>
                          <span className="text-[10px] text-astro-secondary-text block truncate">
                            {panchang.nakshatraEnd}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border relative">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-semibold">
                          {isHi ? 'योग' : 'Yoga'}
                        </span>
                        {panchang.yogaTransitions && panchang.yogaTransitions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setShowAllYogas(prev => !prev)}
                            className="text-[9px] text-astro-accent hover:underline font-medium cursor-pointer"
                          >
                            {showAllYogas ? 'Show Active' : `${panchang.yogaTransitions.length} Yogas ▾`}
                          </button>
                        )}
                      </div>

                      {showAllYogas && panchang.yogaTransitions ? (
                        <div className="space-y-1.5 mt-1 pt-1 border-t border-astro-border/60 animate-fade-in max-h-40 overflow-y-auto">
                          {panchang.yogaTransitions.map((tr, yIdx) => (
                            <div key={yIdx} className="text-[10px] bg-white p-1.5 rounded border border-astro-border/50">
                              <strong className="text-astro-primary font-serif block truncate">{tr.name}</strong>
                              <span className="text-[9px] text-astro-text block font-mono">From: {formatDateTimeWithTz(tr.startDateTime)}</span>
                              <span className="text-[9px] text-astro-text block font-mono">To:   {formatDateTimeWithTz(tr.endDateTime)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <strong className="text-astro-text block text-xs truncate mt-0.5">
                            {panchang.yoga}
                          </strong>
                          <span className="text-[10px] text-astro-secondary-text block truncate">
                            {panchang.yogaEnd || (isHi ? 'शुभ फलदायी' : 'Subha Karma')}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border relative">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-semibold">
                          {isHi ? 'करण' : 'Karana'}
                        </span>
                        {panchang.karanaTransitions && panchang.karanaTransitions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setShowAllKaranas(prev => !prev)}
                            className="text-[9px] text-astro-accent hover:underline font-medium cursor-pointer"
                          >
                            {showAllKaranas ? 'Show Active' : `${panchang.karanaTransitions.length} Karanas ▾`}
                          </button>
                        )}
                      </div>

                      {showAllKaranas && panchang.karanaTransitions ? (
                        <div className="space-y-1.5 mt-1 pt-1 border-t border-astro-border/60 animate-fade-in max-h-40 overflow-y-auto">
                          {panchang.karanaTransitions.map((tr, kIdx) => (
                            <div key={kIdx} className="text-[10px] bg-white p-1.5 rounded border border-astro-border/50">
                              <strong className="text-astro-primary font-serif block truncate">{tr.name} ({tr.type})</strong>
                              <span className="text-[9px] text-astro-text block font-mono">From: {formatDateTimeWithTz(tr.startDateTime)}</span>
                              <span className="text-[9px] text-astro-text block font-mono">To:   {formatDateTimeWithTz(tr.endDateTime)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <strong className="text-astro-text block text-xs truncate mt-0.5">
                            {panchang.karana}
                          </strong>
                          <span className="text-[10px] text-astro-secondary-text block truncate">
                            {panchang.karanaEnd || (isHi ? 'चतुष्पद / चर' : 'Movable')}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border col-span-2 sm:col-span-1">
                      <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-semibold">
                        {isHi ? 'वासर (दिन)' : 'Vaara (Day)'}
                      </span>
                      <strong className="text-astro-primary block text-xs truncate">
                        {panchang.vaara}
                      </strong>
                      <span className="text-[10px] text-astro-muted block">
                        {panchang.paksha}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Fasting & Parana Governance Card */}
                {panchang.hasVrat ? (
                  <div className="bg-astro-bg p-3 sm:p-3.5 rounded-xl border border-astro-accent/40 shadow-2xs space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm">🪔</span>
                        <span className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                          {isHi ? 'व्रत एवं पारणा निर्णय' : 'Fasting & Parana Timings'}
                        </span>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FAF6ED] text-astro-accent border border-astro-accent/30">
                        {panchang.vratName}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border">
                        <span className="text-[10px] font-bold uppercase text-astro-accent block">
                          {isHi ? 'व्रतारम्भ' : 'Fasting Starts'}
                        </span>
                        <span className="text-xs font-bold text-astro-text mt-0.5 block">
                          {panchang.fastingStart}
                        </span>
                        <span className="text-[10px] text-astro-muted">
                          {isHi ? 'सूर्योदय से संकल्प उपवास' : 'Sunrise resolve & fast begins'}
                        </span>
                      </div>

                      <div className="p-2 rounded-lg bg-emerald-50/60 border border-emerald-200">
                        <span className="text-[10px] font-bold uppercase text-astro-primary block">
                          {isHi ? 'व्रत पारणा काल' : 'Fast Breaking (Parana) Window'}
                        </span>
                        <span className="text-xs font-bold text-astro-primary mt-0.5 block">
                          {panchang.paranaWindow}
                        </span>
                        <span className="text-[10px] text-emerald-800">
                          {isHi ? 'द्वादशी समाप्ति से पूर्व' : 'Must break before Dwadashi tithi ends'}
                        </span>
                      </div>
                    </div>

                    <p className="text-[11px] text-astro-secondary-text italic bg-[#FAF6ED] px-2.5 py-1.5 rounded-lg border border-astro-border">
                      <span className="font-semibold text-astro-primary not-italic">{isHi ? 'विधि निर्देश: ' : 'Instruction: '}</span>
                      {panchang.paranaNotes}
                    </p>
                  </div>
                ) : (
                  <div className="bg-astro-bg p-3 sm:p-3.5 rounded-xl border border-astro-border shadow-2xs space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-astro-accent" />
                        <span className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                          {isHi ? 'दैनिक व्रत स्थिति' : 'Daily Observance & Fasting Status'}
                        </span>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {isHi ? 'आज कोई मुख्य व्रत नहीं' : 'No major fast today'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border">
                        <span className="text-[10px] font-bold uppercase text-astro-muted block">
                          {isHi ? 'नित्य उपासना' : 'Daily Deity Archana'}
                        </span>
                        <span className="text-xs font-bold text-astro-primary mt-0.5 block">
                          {isHi ? (panchang.dailyObservanceHi || panchang.dailyObservance) : panchang.dailyObservance}
                        </span>
                      </div>

                      <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border">
                        <span className="text-[10px] font-bold uppercase text-astro-accent block">
                          {isHi ? 'आगामी मुख्य व्रत' : 'Upcoming Major Fast'}
                        </span>
                        <span className="text-xs font-bold text-astro-text mt-0.5 block">
                          {isHi ? (panchang.upcomingFastHi || panchang.upcomingFast) : panchang.upcomingFast}
                        </span>
                      </div>
                    </div>

                    <p className="text-[11px] text-astro-secondary-text italic bg-[#FAF6ED] px-2.5 py-1.5 rounded-lg border border-astro-border">
                      <span className="font-semibold text-astro-primary not-italic">{isHi ? 'पञ्चाङ्ग स्थिति: ' : 'Panchang Note: '}</span>
                      {isHi ? (panchang.panchangNoteHi || panchang.panchangNoteEn) : panchang.panchangNoteEn}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
